import { createApi } from '@reduxjs/toolkit/query/react';
import checkStatus from '../../../../shared/http/checkStatus';
import HttpMethods from '../../../../shared/api/HttpMethods';
import reducerPath from '../../../../app/reducerPath';
import config from '../../../../shared/api/config';
// eslint-disable-next-line boundaries/element-types
import baseQueryWithReAuth from '../../../baseQueryWithReAuth/baseQueryWithReAuth';
// eslint-disable-next-line import/no-named-as-default
import adaptersResourceFromDtoService from '../services/data/adaptersResource-from-dto.service';

const TAG = 'logistic-tasks';
const LOGISTIC_ADVICE_TAG = 'logistic-advices';
export const LOGISTIC_TAG = { type: 'LogisticTask' };
const GEOCODING = 'geocoding';
const url = `/${TAG}`;

// Описываем методы обращения к беку
export const logisticTasksApi = createApi({
  reducerPath: `${reducerPath.logisticsResource}/api`,
  tagTypes: [TAG, 'LogisticTask'],
  baseQuery: (...arg) => baseQueryWithReAuth(...arg, `${config.logisticUrl}`),
  endpoints: (builder) => ({
    getLogisticStates: builder.query({
      query: ({ taskId, producerId }) => ({
        url: `/logistic-tasks-states/${taskId}/cargovolume/${producerId}`,
        method: HttpMethods.GET,
        validateStatus: checkStatus,
      }),
      transformResponse: adaptersResourceFromDtoService,
    }),
    postLogisticsResource: builder.mutation({
      query: ({ body }) => ({
        url: `${url}/import/shortpath`,
        method: HttpMethods.POST,
        body,
        validateStatus: checkStatus,
      }),
      transformResponse: adaptersResourceFromDtoService,
    }),
    postLogisticsResourceVrp: builder.mutation({
      query: ({ body }) => ({
        url: `${url}/import/vrp`,
        method: HttpMethods.POST,
        body,
        validateStatus: checkStatus,
      }),
      transformResponse: adaptersResourceFromDtoService,
    }),
    postLogisticsResourceImport: builder.mutation({
      query: ({ body, taskType }) => ({
        url: `${url}/import/${taskType}`,
        method: HttpMethods.POST,
        body,
        validateStatus: checkStatus,
      }),
    }),
    getLogisticTaskId: builder.query({
      query: ({ id }) => ({
        url: `${url}/${id}`,
        method: HttpMethods.GET,
        validateStatus: checkStatus,
      }),
      providesTags: (_, _err, { id }) => [TAG, { ...LOGISTIC_TAG, id }],
    }),
    getLogisticTaskStatus: builder.query({
      query: ({ id }) => ({
        url: `${url}/${id}/status`,
        method: HttpMethods.GET,
        validateStatus: checkStatus,
      }),
      providesTags: [TAG],
    }),
    patchLogisticTask: builder.mutation({
      query: ({ id, body }) => ({
        url: `${url}/${id}`,
        method: HttpMethods.PATCH,
        body,
        validateStatus: checkStatus,
      }),
      invalidatesTags: [TAG],
    }),
    reorderLogisticTask: builder.mutation({
      query: ({ body }) => ({
        url: `${url}/reorder`,
        method: HttpMethods.POST,
        body,
        validateStatus: checkStatus,
      }),
    }),
    getLogisticTaskSolution: builder.mutation({
      query: ({ body }) => ({
        url: `${url}`,
        method: HttpMethods.GET,
        body,
        validateStatus: checkStatus,
      }),
      invalidatesTags: [TAG],
    }),
    patchLogisticTaskSolve: builder.mutation({
      query: ({ id }) => ({
        url: `${url}/${id}/solve`,
        method: HttpMethods.POST,
        validateStatus: checkStatus,
      }),
      invalidatesTags: [TAG],
    }),
    patchLogisticTaskSolveDelivery: builder.mutation({
      query: ({ id }) => ({
        url: `${url}/resolve/${id}`,
        method: HttpMethods.POST,
        validateStatus: checkStatus,
      }),
    }),
    createVehicleRoutes: builder.mutation({
      query: ({ body, rePlanDate, routeNameTemplate, selectedVehicles, taskName }) => ({
        url: `${url}/create-vehicle-routes`,
        method: HttpMethods.POST,
        body,
        params: {
          routeNameTemplate,
          rePlanDate: rePlanDate || undefined,
          taskName: taskName || undefined,
          selectedVehicles: selectedVehicles || undefined,
        },
        validateStatus: checkStatus,
      }),
    }),
    getLogisticTasks: builder.query({
      query: ({ params }) => ({
        url: `${url}`,
        method: HttpMethods.GET,
        params,
        validateStatus: checkStatus,
      }),
      providesTags: [TAG],
    }),
    getLogisticTasksCount: builder.query({
      query: ({ params }) => ({
        url: `${url}/count`,
        method: HttpMethods.GET,
        params,
        validateStatus: checkStatus,
      }),
      providesTags: [TAG],
    }),
    pauseLogisticTasks: builder.mutation({
      query: ({ id }) => ({
        url: `${url}/${id}/pause`,
        method: HttpMethods.PATCH,
        validateStatus: checkStatus,
      }),
      invalidatesTags: [TAG],
    }),
    stopLogisticTasks: builder.mutation({
      query: ({ id }) => ({
        url: `${url}/${id}/stop`,
        method: HttpMethods.PATCH,
        validateStatus: checkStatus,
      }),
      invalidatesTags: [TAG],
    }),
    startLogisticTasks: builder.mutation({
      query: ({ id }) => ({
        url: `${url}/${id}/start`,
        method: HttpMethods.PATCH,
        validateStatus: checkStatus,
      }),
      invalidatesTags: [TAG],
    }),
    deleteLogisticTasks: builder.mutation({
      query: (id) => ({
        url: `${url}/${id}`,
        method: HttpMethods.DELETE,
        validateStatus: checkStatus,
      }),
      invalidatesTags: [TAG],
    }),
    getLogisticStatesInitial: builder.query({
      query: ({ taskId }) => ({
        url: `/logistic-tasks-states/${taskId}`,
        method: HttpMethods.GET,
        validateStatus: checkStatus,
      }),
      providesTags: [TAG],
      transformResponse: adaptersResourceFromDtoService,
    }),
    getGeoZoneIcon: builder.query({
      query: ({ logisticTaskId, geozoneId, width = 76, height = 76 }) => ({
        url: `/logistic-tasks/${logisticTaskId}/geo-zones/${geozoneId}/icon`,
        method: HttpMethods.GET,
        params: { width, height },
        responseHandler: (response) => response.blob(),
        validateStatus: checkStatus,
      }),
    }),
    getLogisticTaskAdviceStats: builder.query({
      query: ({ id }) => ({
        url: `/logistic-tasks/${id}/advices-stats`,
        method: HttpMethods.GET,
        validateStatus: checkStatus,
      }),
    }),
    changeLogisticVehicleState: builder.mutation({
      query: ({ logisticTaskId, vehicleId, state }) => ({
        url: `${url}/${logisticTaskId}/vehicle/${vehicleId}`,
        method: HttpMethods.PUT,
        params: { state },
        validateStatus: checkStatus,
      }),
      invalidatesTags: [TAG],
    }),
    deleteHarvester: builder.mutation({
      query: ({ logisticTaskId, vehicleId }) => ({
        url: `${url}/${logisticTaskId}/producer/${vehicleId}`,
        method: HttpMethods.DELETE,
        validateStatus: checkStatus,
      }),
      invalidatesTags: [TAG],
    }),
    deleteVehicle: builder.mutation({
      query: ({ logisticTaskId, vehicleId }) => ({
        url: `${url}/${logisticTaskId}/vehicle/${vehicleId}`,
        method: HttpMethods.DELETE,
        validateStatus: checkStatus,
      }),
      invalidatesTags: [TAG],
    }),
    getLogisticAdvices: builder.query({
      query: ({ params }) => ({
        url: `/logistic-advices`,
        method: HttpMethods.GET,
        params,
        validateStatus: checkStatus,
      }),
      providesTags: [LOGISTIC_ADVICE_TAG],
    }),
    getLogisticAdvicesCount: builder.query({
      query: ({ params }) => ({
        url: `/logistic-advices/count`,
        method: HttpMethods.GET,
        params,
        validateStatus: checkStatus,
      }),
      providesTags: [LOGISTIC_ADVICE_TAG],
    }),
    updateLogisticAdvice: builder.mutation({
      query: ({ id, body }) => ({
        url: `/logistic-advices/${id}`,
        method: HttpMethods.PATCH,
        body,
        validateStatus: checkStatus,
      }),
      invalidatesTags: [LOGISTIC_ADVICE_TAG],
    }),

    startWarehouse: builder.mutation({
      query: ({ logisticTaskId, warehouseId }) => ({
        url: `${url}/${logisticTaskId}/warehouse/${warehouseId}/start`,
        method: HttpMethods.PATCH,
        validateStatus: checkStatus,
      }),
      invalidatesTags: [TAG],
    }),
    stopWarehouse: builder.mutation({
      query: ({ logisticTaskId, warehouseId }) => ({
        url: `${url}/${logisticTaskId}/warehouse/${warehouseId}/stop`,
        method: HttpMethods.PATCH,
        validateStatus: checkStatus,
      }),
      invalidatesTags: [TAG],
    }),
  }),
});

export const geocodeApi = createApi({
  reducerPath: `${reducerPath.geocodeResource}/api`,
  tagTypes: [GEOCODING],
  baseQuery: (...arg) => baseQueryWithReAuth(...arg, `${config.geocodingUrl}`),
  endpoints: (builder) => ({
    findAddress: builder.query({
      query: (address) => ({
        url: `/search`,
        method: HttpMethods.POST,
        body: [address],
        validateStatus: checkStatus,
      }),
      providesTags: [GEOCODING],
    }),
  }),
});

export const {
  useGetLogisticTaskIdQuery,
  useLazyGetLogisticTaskIdQuery,
  useGetLogisticTaskStatusQuery,
  useLazyGetLogisticTaskStatusQuery,
  usePatchLogisticTaskMutation,
  useReorderLogisticTaskMutation,
  useGetLogisticTaskSolutionMutation,
  usePostLogisticsResourceMutation,
  usePostLogisticsResourceVrpMutation,
  usePostLogisticsResourceImportMutation,
  usePatchLogisticTaskSolveMutation,
  usePatchLogisticTaskSolveDeliveryMutation,
  useGetLogisticTasksQuery,
  useLazyGetLogisticTasksCountQuery,
  useChangeLogisticVehicleStateMutation,
  useDeleteHarvesterMutation,
  useDeleteVehicleMutation,
  useLazyGetLogisticTasksQuery,
  usePauseLogisticTasksMutation,
  useStartLogisticTasksMutation,
  useLazyDeleteLogisticTasksQuery,
  useDeleteLogisticTasksMutation,
  useStopLogisticTasksMutation,
  useCreateVehicleRoutesMutation,
  useLazyGetLogisticStatesQuery,
  useGetLogisticStatesInitialQuery,
  useGetGeoZoneIconQuery,
  useGetLogisticTaskAdviceStatsQuery,
  useGetLogisticAdvicesQuery,
  useLazyGetLogisticAdvicesQuery,
  useGetLogisticAdvicesCountQuery,
  useUpdateLogisticAdviceMutation,
  useStartWarehouseMutation,
  useStopWarehouseMutation,
} = logisticTasksApi;

export const { useLazyFindAddressQuery } = geocodeApi;
